"use client";

import { Menu, Phone, X } from "lucide-react";
import { useState } from "react";
import { useQuote } from "./quote-context";
import { BrandLogo } from "./brand-logo";

const NAV = [
  { label: "Home", href: "#top" },
  { label: "Products & Services", href: "#categories" },
  { label: "Our Work", href: "#work" },
  { label: "About", href: "#story" },
  { label: "Contact", href: "#quote" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const { goToQuote } = useQuote();

  const jump = (href: string) => {
    setOpen(false);
    const el = document.querySelector(href);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* Announcement bar */}
      <div className="relative z-50 border-b border-ink/10 bg-ivory text-ink">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-center gap-1 px-5 py-2.5 text-center sm:flex-row sm:gap-4">
          <p className="text-[0.72rem] tracking-wide text-ink/75">
            Custom engraving for businesses, events, and meaningful gifts.
          </p>
          <button
            onClick={() => goToQuote()}
            className="text-[0.72rem] font-semibold text-brass-deep underline-offset-4 transition hover:underline"
          >
            Have a project in mind? Request a quote →
          </button>
        </div>
      </div>

      <header className="site-header sticky top-0 z-50 bg-softwhite">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4">
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              jump("#top");
            }}
            className="flex items-center"
            aria-label="Mills Engraving home"
          >
            <BrandLogo className="h-11 w-auto sm:h-14" />
          </a>

          <nav aria-label="Primary" className="hidden items-center gap-8 lg:flex">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={(e) => {
                  e.preventDefault();
                  jump(n.href);
                }}
                className="group relative text-sm font-medium text-ink/75 transition hover:text-ink"
              >
                {n.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-full origin-right scale-x-0 bg-brass-deep transition-transform duration-300 group-hover:origin-left group-hover:scale-x-100" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => goToQuote()}
              className="hidden rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-softwhite transition hover:bg-ink/85 sm:inline-flex"
            >
              Request a Quote
            </button>
            <button
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="inline-flex size-10 items-center justify-center rounded-full border border-ink/20 text-ink lg:hidden"
            >
              <Menu className="size-5" />
            </button>
          </div>
        </div>
      </header>

      {open && (
          <div className="fixed inset-0 z-[60] bg-softwhite text-ink">
            <div className="flex items-center justify-between px-5 py-5">
              <BrandLogo className="h-10 w-auto" sizes="120px" />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex size-10 items-center justify-center rounded-full border border-ink/20"
              >
                <X className="size-5" />
              </button>
            </div>
            <nav aria-label="Mobile" className="mt-6 flex flex-col px-5">
              {NAV.map((n) => (
                <button
                  key={n.label}
                  onClick={() => jump(n.href)}
                  className="border-b border-ink/10 py-5 text-left font-display text-3xl font-semibold"
                >
                  {n.label}
                </button>
              ))}
            </nav>
            <div className="mt-10 space-y-3 px-5">
              <button
                onClick={() => {
                  setOpen(false);
                  goToQuote();
                }}
                className="w-full rounded-full bg-ink px-6 py-4 font-semibold text-softwhite"
              >
                Request a Custom Quote
              </button>
              <a
                href="tel:+12047613698"
                className="flex w-full items-center justify-center gap-2 rounded-full border border-ink/20 px-6 py-4 font-semibold text-ink"
              >
                <Phone className="size-4" /> 204-761-3698
              </a>
            </div>
          </div>
      )}
    </>
  );
}